'use client';

import { useState, useCallback, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@flowstack/sdk';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import PrivyLoginButton from './PrivyLoginButton';
import { POST_AUTH_HOME } from '@/src/lib/flags';

export default function RegisterForm({ onSwitchMode }: { onSwitchMode?: () => void } = {}) {
  const navigate = useNavigate();
  const { register, error: authError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setError('');
      if (password !== confirm) {
        setError('Passwords do not match.');
        return;
      }
      if (password.length < 8) {
        setError('Password must be at least 8 characters.');
        return;
      }
      setLoading(true);
      try {
        const ok = await register(email, password);
        if (ok) {
          navigate(POST_AUTH_HOME);
        } else {
          // Surface the SDK error — detect 409 "already exists" from the message
          const sdkError = (authError ?? '').toLowerCase();
          if (sdkError.includes('409') || sdkError.includes('already') || sdkError.includes('exist')) {
            setError('An account with this email already exists. Sign in instead.');
          } else {
            setError(authError ?? 'Registration failed. Please try again.');
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Registration failed. Please try again.');
      } finally {
        setLoading(false);
      }
    },
    [email, password, confirm, register, navigate],
  );

  return (
    <div className="w-full max-w-sm mx-auto">
      <div className="mb-8 text-center">
        <h1 className="text-2xl font-semibold text-[var(--color-text)] mb-1">Start building</h1>
        <p className="text-sm text-[var(--color-text-secondary)]">Real users. Real data. Live in 3 minutes.</p>
      </div>

      {/* Primary: Privy embedded wallet login */}
      <PrivyLoginButton label="Get started" className="mb-4" />

      {/* Secondary: email/password — collapsed by default */}
      {!showEmailForm ? (
        <button
          type="button"
          onClick={() => setShowEmailForm(true)}
          className="w-full mt-3 text-xs text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)] transition-colors"
        >
          Register with email &amp; password
        </button>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-4">
          <Input
            id="email"
            type="email"
            label="Email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
          <Input
            id="password"
            type="password"
            label="Password"
            placeholder="Min. 8 characters"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="new-password"
          />
          <Input
            id="confirm"
            type="password"
            label="Confirm password"
            placeholder="Re-enter your password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            autoComplete="new-password"
          />

          {error && (
            <p className="text-sm text-[var(--color-error)] bg-[var(--color-error-light)] px-3 py-2 rounded-lg">
              {error}
            </p>
          )}

          <Button type="submit" loading={loading} size="lg" className="w-full">
            Create account
          </Button>
        </form>
      )}

      <p className="mt-6 text-center text-sm text-[var(--color-text-secondary)]">
        Already have an account?{' '}
        {onSwitchMode ? (
          <button type="button" onClick={onSwitchMode} className="text-[var(--color-accent)] hover:underline font-medium">
            Sign in
          </button>
        ) : (
          <Link to="/login" className="text-[var(--color-accent)] hover:underline font-medium">
            Sign in
          </Link>
        )}
      </p>
    </div>
  );
}
